import React from 'react';
import { connect } from 'react-redux';
import { withStyles } from 'tss-react/mui';
import { isMobileBrowser } from '../../../base/environment/utils';
import { getVideospaceFloatingElementsBottomSpacing, pixelsToRem, remToPixels } from '../../../base/ui/functions.web';
import { getStageParticipantNameLabelHeight } from '../../../display-name/components/web/styles';
import { shouldDisplayStageParticipantBadge } from '../../../display-name/functions';
import { getTransitionParamsForElementsAboveToolbox, isToolboxVisible, toCSSTransitionValue } from '../../../toolbox/functions.web';
import { calculateSubtitlesFontSize } from '../../functions.web';
import { AbstractCaptions, _abstractMapStateToProps } from '../AbstractCaptions';
/**
 * Semi-transparent black fill for the on-stage subtitle bar.
 */
const SUBTITLE_BAR_BACKGROUND = 'rgba(0, 0, 0, 0.7)';
/**
 * Horizontal inset padding inside the subtitle bar (px added to font-based padding).
 */
const SUBTITLE_BAR_HORIZONTAL_PADDING_PX = 16;
/**
 * Narrow/mobile horizontal inset so the bar can use more of the stage width.
 */
const SUBTITLE_BAR_HORIZONTAL_PADDING_NARROW_PX = 8;
/**
 * Corner radius of the on-stage subtitle bar.
 */
const SUBTITLE_BAR_BORDER_RADIUS_PX = 8;
/**
 * Desktop: share of #largeVideoContainer (already excludes vertical filmstrip).
 * Do not use vw — viewport width ignores filmstrip / side panel / mobile chrome.
 */
const SUBTITLE_BAR_MAX_WIDTH_DESKTOP = '70%';
/**
 * Narrow/mobile: almost full stage width with 12px gutters on each side.
 */
const SUBTITLE_BAR_MAX_WIDTH_NARROW = 'calc(100% - 24px)';
/**
 * Gap between the on-stage participant name badge and the subtitle bar (px).
 */
const SUBTITLE_TO_BADGE_GAP_PX = 10;
/**
 * Extra bottom margin when the toolbox is shifted up (px).
 */
const TOOLBOX_SHIFT_UP_MARGIN_PX = 30;
/**
 * z-index below popups (8) so captions stay under dialogs.
 */
const SUBTITLE_BAR_Z_INDEX = 7;
/**
 * Returns the max width of the on-stage subtitle bar.
 *
 * @param {boolean} isNarrow - Whether the layout is mobile/narrow.
 * @returns {string}
 */
function getSubtitleBarMaxWidth(isNarrow) {
    return isNarrow ? SUBTITLE_BAR_MAX_WIDTH_NARROW : SUBTITLE_BAR_MAX_WIDTH_DESKTOP;
}
/**
 * Inline styles that must win over global `p` / filmstrip CSS. Keep the bar to one line.
 */
const SUBTITLE_TEXT_INLINE_STYLE = {
    background: 'transparent',
    color: '#fff',
    display: 'inline-block',
    flex: '0 0 auto',
    overflow: 'hidden',
    overflowWrap: 'normal',
    whiteSpace: 'nowrap',
    wordBreak: 'keep-all'
};
const styles = (theme, props) => {
    const { _isLifted = false, _clientHeight, _isNarrow = false, _shiftUp = false, _toolboxVisible = false } = props;
    const fontSize = calculateSubtitlesFontSize(_clientHeight);
    // Normally we would use 0.2 * fontSize in order to cover the background gap from line-height: 1.2 but it seems
    // the current font is a little bit larger than it is supposed to be.
    const padding = 0.1 * fontSize;
    const horizontalPadding = _isNarrow
        ? SUBTITLE_BAR_HORIZONTAL_PADDING_NARROW_PX
        : SUBTITLE_BAR_HORIZONTAL_PADDING_PX;
    const lineHeightPx = 1.2 * fontSize;
    let bottom = getVideospaceFloatingElementsBottomSpacing(theme, _toolboxVisible);
    let marginBottom = 0;
    // This is the case where we display the onstage participant display name
    // below the subtitles.
    if (_isLifted) {
        // Space between the onstage participant display name label and subtitles, plus subtitle padding.
        bottom += remToPixels(getStageParticipantNameLabelHeight(theme, _clientHeight))
            + SUBTITLE_TO_BADGE_GAP_PX + padding;
    }
    if (_shiftUp) {
        marginBottom += TOOLBOX_SHIFT_UP_MARGIN_PX;
    }
    return {
        transcriptionSubtitles: {
            bottom: `${bottom}px`,
            marginBottom: `${marginBottom}px`,
            fontSize: pixelsToRem(fontSize),
            left: '50%',
            maxWidth: getSubtitleBarMaxWidth(_isNarrow),
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            wordBreak: 'keep-all',
            overflowWrap: 'normal',
            height: `${lineHeightPx + (2 * padding)}px`,
            width: 'fit-content',
            pointerEvents: 'none',
            position: 'absolute',
            transform: 'translateX(-50%)',
            zIndex: SUBTITLE_BAR_Z_INDEX,
            lineHeight: 1.2,
            transition: `bottom ${toCSSTransitionValue(getTransitionParamsForElementsAboveToolbox(_toolboxVisible))}`,
            background: SUBTITLE_BAR_BACKGROUND,
            borderRadius: `${SUBTITLE_BAR_BORDER_RADIUS_PX}px`,
            padding: `${padding}px ${horizontalPadding}px`
        }
    };
};
/**
 * React {@code Component} which can display speech-to-text results from
 * Jigasi as subtitles.
 */
class Captions extends AbstractCaptions {
    /**
     * Renders the transcription text.
     *
     * @param {string} id - The ID of the transcript message from which the
     * {@code text} has been created.
     * @param {string} text - Subtitles text formatted with the participant's
     * name.
     * @protected
     * @returns {ReactElement} - The React element which displays the text.
     */
    _renderParagraph(id, text) {
        return (React.createElement("span", { key: id, style: SUBTITLE_TEXT_INLINE_STYLE }, text));
    }
    /**
     * Renders the subtitles container.
     *
     * @param {Array<ReactElement>} paragraphs - An array of elements created
     * for each subtitle using the {@link _renderParagraph} method.
     * @protected
     * @returns {ReactElement} - The subtitles container.
     */
    _renderSubtitlesContainer(paragraphs) {
        const classes = withStyles.getClasses(this.props);
        const { _clientHeight, _isNarrow } = this.props;
        const fontSize = calculateSubtitlesFontSize(_clientHeight);
        const padding = 0.1 * fontSize;
        const horizontalPadding = _isNarrow
            ? SUBTITLE_BAR_HORIZONTAL_PADDING_NARROW_PX
            : SUBTITLE_BAR_HORIZONTAL_PADDING_PX;
        const lineHeightPx = 1.2 * fontSize;
        // Inline styles beat global / tss-nested rules that previously let <p> wrap.
        const lineLockStyle = {
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
            height: `${lineHeightPx + (2 * padding)}px`,
            justifyContent: 'flex-end',
            maxWidth: getSubtitleBarMaxWidth(_isNarrow),
            overflow: 'hidden',
            overflowWrap: 'normal',
            padding: `${padding}px ${horizontalPadding}px`,
            whiteSpace: 'nowrap',
            width: 'fit-content',
            wordBreak: 'keep-all'
        };
        return (React.createElement("div", { className: classes.transcriptionSubtitles, style: lineLockStyle }, paragraphs[paragraphs.length - 1]));
    }
}
/**
 * Maps (parts of) the redux state to the associated {@code }'s
 * props.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {Object}
 */
function mapStateToProps(state) {
    const { clientHeight, isNarrowLayout } = state['features/base/responsive-ui'];
    return {
        ..._abstractMapStateToProps(state),
        _isLifted: shouldDisplayStageParticipantBadge(state),
        _clientHeight: clientHeight,
        _isNarrow: Boolean(isNarrowLayout) || isMobileBrowser(),
        _shiftUp: state['features/toolbox'].shiftUp,
        _toolboxVisible: isToolboxVisible(state)
    };
}
export default connect(mapStateToProps)(withStyles(Captions, styles));
